import { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Users, Shield, Plus, Pencil, Trash2, Check, X, UserPlus, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/lib/auth';

const MODULES = [
  { id: 'DASHBOARD', label: 'Tableau de bord' },
  { id: 'MENU', label: 'Menu' },
  { id: 'HOURS', label: 'Horaires' },
  { id: 'PHOTOS', label: 'Photos' },
  { id: 'ACTUS', label: 'Actualités' },
  { id: 'REVIEWS', label: 'Avis clients' },
  { id: 'HOMEPAGE_SECTIONS', label: 'Page d\'accueil' },
  { id: 'RESERVATIONS', label: 'Réservations' },
  { id: 'ORDERS', label: 'Commandes' },
  { id: 'SETTINGS_CONTENT', label: 'Paramètres (contenu)' },
  { id: 'SETTINGS_PAYMENT', label: 'Paramètres (paiement)' },
  { id: 'SETTINGS_SECURITY', label: 'Paramètres (sécurité)' },
  { id: 'SETTINGS_MAINTENANCE', label: 'Paramètres (maintenance)' },
  { id: 'SETTINGS_RESERVATION', label: 'Paramètres (réservation)' },
  { id: 'ADMIN_USERS', label: 'Gestion utilisateurs' },
  { id: 'MODULES_MANAGEMENT', label: 'Gestion modules' },
];

export default function UsersManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [userForm, setUserForm] = useState({ email: '', name: '', role_id: '', is_active: true });
  const [roleForm, setRoleForm] = useState({ name: '', description: '' });
  const [rolePermissions, setRolePermissions] = useState({});
  const [settings, setSettings] = useState({});
  const [newEmail, setNewEmail] = useState('');

  const loadData = async () => {
    try {
      const [usersRes, rolesRes, settingsRes] = await Promise.all([
        adminApi.getUsers(),
        adminApi.getRoles(),
        adminApi.getSettings(),
      ]);
      setUsers(usersRes.data || []);
      setRoles(rolesRes.data || []);
      setSettings(settingsRes.data || {});
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  // User handlers
  const openNewUser = () => {
    setEditingUser(null);
    setUserForm({ email: '', name: '', role_id: roles.find(r => r.name === 'GERANT')?.id || '', is_active: true });
    setUserDialogOpen(true);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({ email: user.email, name: user.name, role_id: user.role_id, is_active: user.is_active });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.email || !userForm.name) { toast.error('Champs requis'); return; }
    try {
      if (editingUser) {
        await adminApi.updateUser(editingUser.id, { name: userForm.name, role_id: userForm.role_id, is_active: userForm.is_active });
        toast.success('Utilisateur modifié');
      } else {
        await adminApi.createUser(userForm);
        toast.success('Utilisateur créé');
      }
      setUserDialogOpen(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await adminApi.deleteUser(userId);
      toast.success('Utilisateur supprimé');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Role handlers
  const openNewRole = () => {
    setEditingRole(null);
    setRoleForm({ name: '', description: '' });
    setRolePermissions({});
    setRoleDialogOpen(true);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({ name: role.name, description: role.description });
    setRolePermissions(role.permissions || {});
    setRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name) { toast.error('Nom requis'); return; }
    try {
      if (editingRole) {
        // Update permissions
        const perms = MODULES.map(m => ({
          module: m.id,
          can_view: rolePermissions[m.id]?.can_view || false,
          can_edit: rolePermissions[m.id]?.can_edit || false
        }));
        await adminApi.updateRolePermissions(editingRole.id, perms);
        toast.success('Permissions mises à jour');
      } else {
        await adminApi.createRole(roleForm);
        toast.success('Rôle créé');
      }
      setRoleDialogOpen(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Supprimer ce rôle ?')) return;
    try {
      await adminApi.deleteRole(roleId);
      toast.success('Rôle supprimé');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const togglePermission = (module, action) => {
    setRolePermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]: !prev[module]?.[action]
      }
    }));
  };

  // Whitelist management
  const addEmail = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Email invalide'); return; }
    const current = settings.allowed_emails || [];
    if (current.includes(email)) { toast.error('Email déjà dans la liste'); return; }
    try {
      await adminApi.updateSettings({ allowed_emails: [...current, email] });
      setSettings(prev => ({ ...prev, allowed_emails: [...current, email] }));
      setNewEmail('');
      toast.success('Email ajouté');
    } catch { toast.error('Erreur'); }
  };

  const removeEmail = async (email) => {
    const updated = (settings.allowed_emails || []).filter(e => e !== email);
    try {
      await adminApi.updateSettings({ allowed_emails: updated });
      setSettings(prev => ({ ...prev, allowed_emails: updated }));
      toast.success('Email retiré');
    } catch { toast.error('Erreur'); }
  };

  return (
    <AdminLayout title="Utilisateurs et rôles">
      <div className="space-y-6" data-testid="admin-users">
        <Tabs defaultValue="users">
          <TabsList className="bg-garden-100 mb-6">
            <TabsTrigger value="users" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5">
              <Users size={14} /> Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="roles" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5">
              <Shield size={14} /> Rôles
            </TabsTrigger>
            <TabsTrigger value="access" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white gap-1.5">
              <ShieldCheck size={14} /> Accès
            </TabsTrigger>
          </TabsList>

          {/* UTILISATEURS */}
          <TabsContent value="users">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-charcoal/60">Gérez les utilisateurs qui peuvent accéder à l'administration</p>
              <Button onClick={openNewUser} className="bg-garden-800 hover:bg-garden-900 text-white rounded-full" data-testid="add-user-btn">
                <UserPlus size={16} className="mr-1" /> Ajouter un utilisateur
              </Button>
            </div>

            {loading ? (
              <div className="bg-white rounded-xl border border-garden-200 p-5 animate-pulse">
                <div className="h-20 bg-garden-50 rounded" />
              </div>
            ) : users.length === 0 ? (
              <div className="bg-white rounded-xl border border-garden-200 p-10 text-center">
                <Users size={48} className="mx-auto text-garden-300 mb-4" />
                <p className="text-charcoal/60">Aucun utilisateur</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-garden-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-garden-50 border-b border-garden-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-garden-800">Utilisateur</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-garden-800">Rôle</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-garden-800">Statut</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-garden-800">Dernière connexion</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id} className="border-b border-garden-100 last:border-0" data-testid={`user-row-${user.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-charcoal">{user.name}</div>
                          <div className="text-sm text-charcoal/60">{user.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.role_name === 'ADMIN' ? 'bg-purple-100 text-purple-700' : 'bg-garden-100 text-garden-700'
                          }`}>
                            {user.role_name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {user.is_active ? (
                            <span className="flex items-center gap-1 text-green-600 text-sm"><Check size={14} /> Actif</span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-500 text-sm"><X size={14} /> Inactif</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-charcoal/60">
                          {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => openEditUser(user)} className="h-8 w-8 p-0">
                              <Pencil size={14} />
                            </Button>
                            {user.id !== currentUser?.id && (
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(user.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-600">
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* RÔLES */}
          <TabsContent value="roles">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-charcoal/60">Définissez les permissions pour chaque rôle</p>
              <Button onClick={openNewRole} className="bg-garden-800 hover:bg-garden-900 text-white rounded-full" data-testid="add-role-btn">
                <Plus size={16} className="mr-1" /> Nouveau rôle
              </Button>
            </div>

            <div className="grid gap-4">
              {roles.map(role => (
                <div key={role.id} className="bg-white rounded-xl border border-garden-200 p-5" data-testid={`role-${role.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-heading font-semibold text-garden-800 flex items-center gap-2">
                        {role.name}
                        {role.is_system && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Système</span>}
                      </h3>
                      <p className="text-sm text-charcoal/60">{role.description}</p>
                    </div>
                    {!role.is_system && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditRole(role)}>
                          <Pencil size={14} className="mr-1" /> Permissions
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteRole(role.id)} className="text-red-500 hover:text-red-600">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                  {role.is_system && (
                    <p className="text-xs text-charcoal/50 mt-2">Les permissions de ce rôle ne peuvent pas être modifiées.</p>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ACCÈS (Liste blanche) */}
          <TabsContent value="access">
            <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
              <div className="flex items-start gap-3">
                <ShieldCheck size={20} className="text-garden-600 mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-heading font-semibold text-garden-800">Liste blanche des administrateurs</h3>
                  <p className="text-xs text-charcoal/60 mt-1">
                    Seules les adresses email listées ci-dessous pourront se connecter à l'administration. Si la liste est vide, tous les comptes Google sont autorisés.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addEmail()}
                  className="border-garden-200 rounded-xl flex-1"
                  placeholder="email@exemple.com"
                  data-testid="whitelist-email-input"
                />
                <Button onClick={addEmail} data-testid="whitelist-add-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full px-6">
                  <Plus size={16} className="mr-1" /> Ajouter
                </Button>
              </div>
              {(settings.allowed_emails || []).length > 0 ? (
                <div className="space-y-2" data-testid="whitelist-list">
                  {(settings.allowed_emails || []).map((email) => (
                    <div key={email} className="flex items-center justify-between bg-garden-50 rounded-xl px-4 py-3" data-testid={`whitelist-item-${email}`}>
                      <span className="text-sm text-charcoal font-medium">{email}</span>
                      <button onClick={() => removeEmail(email)} className="text-red-400 hover:text-red-600 p-1" data-testid={`whitelist-remove-${email}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3" data-testid="whitelist-empty-warning">
                  <p className="text-xs text-yellow-800">
                    <strong>Attention :</strong> La liste est vide. Tout compte Google peut accéder à l'administration.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* User Dialog */}
        <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-garden-800">
                {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm">Email (compte Google)</Label>
                <Input 
                  type="email"
                  value={userForm.email} 
                  onChange={e => setUserForm({ ...userForm, email: e.target.value })} 
                  className="border-garden-200 rounded-xl"
                  disabled={!!editingUser}
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <Label className="text-sm">Nom</Label>
                <Input 
                  value={userForm.name} 
                  onChange={e => setUserForm({ ...userForm, name: e.target.value })} 
                  className="border-garden-200 rounded-xl"
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <Label className="text-sm">Rôle</Label>
                <Select value={userForm.role_id} onValueChange={v => setUserForm({ ...userForm, role_id: v })}>
                  <SelectTrigger className="border-garden-200 rounded-xl" data-testid="user-role-select">
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Compte actif</Label>
                <Switch 
                  checked={userForm.is_active} 
                  onCheckedChange={v => setUserForm({ ...userForm, is_active: v })}
                  data-testid="user-active-switch"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => setUserDialogOpen(false)} className="flex-1 rounded-full">Annuler</Button>
                <Button onClick={handleSaveUser} className="flex-1 bg-garden-800 hover:bg-garden-900 text-white rounded-full" data-testid="user-save-btn">
                  {editingUser ? 'Enregistrer' : 'Créer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Role Dialog */}
        <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-garden-800">
                {editingRole ? `Permissions du rôle "${editingRole.name}"` : 'Nouveau rôle'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {!editingRole && (
                <>
                  <div>
                    <Label className="text-sm">Nom du rôle</Label>
                    <Input 
                      value={roleForm.name} 
                      onChange={e => setRoleForm({ ...roleForm, name: e.target.value })} 
                      className="border-garden-200 rounded-xl"
                      placeholder="Ex: Editeur, Manager..."
                      data-testid="role-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Description</Label>
                    <Input 
                      value={roleForm.description} 
                      onChange={e => setRoleForm({ ...roleForm, description: e.target.value })} 
                      className="border-garden-200 rounded-xl"
                    />
                  </div>
                </>
              )}
              
              {editingRole && (
                <div className="border border-garden-200 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-garden-50 border-b border-garden-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-garden-800">Module</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-garden-800">Voir</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-garden-800">Modifier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MODULES.map(mod => (
                        <tr key={mod.id} className="border-b border-garden-100 last:border-0">
                          <td className="px-4 py-3 text-sm text-charcoal">{mod.label}</td>
                          <td className="px-4 py-3 text-center">
                            <Switch 
                              checked={rolePermissions[mod.id]?.can_view || false}
                              onCheckedChange={() => togglePermission(mod.id, 'can_view')}
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Switch 
                              checked={rolePermissions[mod.id]?.can_edit || false}
                              onCheckedChange={() => togglePermission(mod.id, 'can_edit')}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => setRoleDialogOpen(false)} className="flex-1 rounded-full">Annuler</Button>
                <Button onClick={handleSaveRole} className="flex-1 bg-garden-800 hover:bg-garden-900 text-white rounded-full" data-testid="role-save-btn">
                  Enregistrer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
